import { CreateEventDto, UpdateEventDto, JoinEventDto } from '@sportspredict/shared';
import type {
	CreateHomeWaitlistRequest,
	HomeEventDto,
	HomeWaitlistDto,
	ValidateHomeEventAccessRequest,
} from '@/types';
import api from './base-api';

interface GetEventsParams {
	q?: Record<string, unknown>;
	sort?: string;
	limit?: number;
	skip?: number;
	fields?: string;
}

const HOME_EVENT_TAGS = {
	featured: 'HOME_FEATURED',
	crowdFavorites: 'HOME_CROWD_FAVORITES',
} as const;

const getHomeEventTags = (events: HomeEventDto[] | undefined, listId: string) => [
	{ type: 'Event' as const, id: listId },
	...(events?.map(({ id }) => ({ type: 'Event' as const, id })) ?? []),
];

const markEventAsWaitlisted = (events: HomeEventDto[], eventId: string): void => {
	const event = events.find(({ id }) => id === eventId);

	if (event) {
		event.isInWaitlist = true;
		event.subscriber_count = (event.subscriber_count ?? 0) + 1;
	}
};

const eventsApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getEvent: builder.query({
			query: (id: string) => ({
				url: `events/${id}`,
				method: 'GET',
			}),
			providesTags: (_result, _error, id: string) => [{ type: 'Event', id }],
			extraOptions: { skipAuth: true },
		}),
		getEvents: builder.query({
			query: (params?: GetEventsParams) => {
				const queryParams: Record<string, string> = {};
				if (params?.q && Object.keys(params.q).length > 0) {
					queryParams.q = JSON.stringify(params.q);
				}
				if (params?.sort) queryParams.sort = params.sort;
				if (params?.limit != null) queryParams.limit = String(params.limit);
				if (params?.skip != null) queryParams.skip = String(params.skip);
				if (params?.fields) queryParams.fields = params.fields;
				return {
					url: 'events',
					method: 'GET',
					params: Object.keys(queryParams).length > 0 ? queryParams : undefined,
				};
			},
			providesTags: [{ type: 'Event', id: 'LIST' }],
			extraOptions: { skipAuth: true },
		}),
		getUserEvents: builder.query({
			query: (userId?: string) => ({
				url: userId
					? `events/user-events?userId=${encodeURIComponent(userId)}`
					: 'events/user-events',
				method: 'GET',
			}),
			providesTags: [{ type: 'Event', id: 'USER_LIST' }],
		}),
		getUserEventsSummary: builder.query({
			query: (userId: string) => ({
				url: `events/user-events/summary?userId=${encodeURIComponent(userId)}`,
				method: 'GET',
			}),
			providesTags: [{ type: 'Event', id: 'USER_SUMMARY' }],
		}),
		getGameGroups: builder.query({
			query: (catId: string) => ({
				url: `events/game-groups/${catId}`,
				method: 'GET',
			}),
		}),
		refreshGamesListByCategory: builder.query({
			query: (catId: string) => ({
				url: `events/refresh-games-list/${catId}`,
				method: 'GET',
			}),
		}),
		createEvent: builder.mutation({
			query: (data: CreateEventDto) => ({
				url: 'events',
				method: 'POST',
				body: data,
			}),
			invalidatesTags: [{ type: 'Event', id: 'LIST' }],
		}),
		updateEvent: builder.mutation({
			query: ({ id, ...rest }: UpdateEventDto) => ({
				url: 'events',
				method: 'PUT',
				body: { id, ...rest },
			}),
			invalidatesTags: (_result, _error, { id }: UpdateEventDto) => [
				{ type: 'Event', id },
				{ type: 'Event', id: 'LIST' },
			],
		}),
		deleteEvent: builder.mutation({
			query: (id: string) => ({
				url: `events/${id}`,
				method: 'DELETE',
			}),
			invalidatesTags: [{ type: 'Event', id: 'LIST' }],
		}),
		joinEvent: builder.mutation({
			query: (data: JoinEventDto) => ({
				url: 'events/join',
				method: 'POST',
				body: data,
			}),
			invalidatesTags: [{ type: 'Event', id: 'USER_LIST' }],
		}),
		getPublicFeaturedEvents: builder.query<HomeEventDto[], void>({
			query: () => ({ url: 'events/featured' }),
			providesTags: (result) => getHomeEventTags(result, HOME_EVENT_TAGS.featured),
			extraOptions: { skipAuth: true },
		}),
		getFeaturedEventsForUser: builder.query<HomeEventDto[], void>({
			query: () => ({ url: 'events/featured/me' }),
			providesTags: (result) => getHomeEventTags(result, HOME_EVENT_TAGS.featured),
		}),
		getPublicCrowdFavoriteEvents: builder.query<HomeEventDto[], void>({
			query: () => ({ url: 'events/crowd-favorites' }),
			providesTags: (result) => getHomeEventTags(result, HOME_EVENT_TAGS.crowdFavorites),
			extraOptions: { skipAuth: true },
		}),
		getCrowdFavoriteEventsForUser: builder.query<HomeEventDto[], void>({
			query: () => ({ url: 'events/crowd-favorites/me' }),
			providesTags: (result) => getHomeEventTags(result, HOME_EVENT_TAGS.crowdFavorites),
		}),
		createHomeWaitlist: builder.mutation<HomeWaitlistDto, CreateHomeWaitlistRequest>({
			query: (body) => ({
				url: 'waitlists',
				method: 'POST',
				body,
			}),
			async onQueryStarted({ event_id: eventId }, { dispatch, queryFulfilled }) {
				try {
					await queryFulfilled;

					dispatch(
						eventsApi.util.updateQueryData('getFeaturedEventsForUser', undefined, (events) => {
							markEventAsWaitlisted(events, eventId);
						})
					);
					dispatch(
						eventsApi.util.updateQueryData('getCrowdFavoriteEventsForUser', undefined, (events) => {
							markEventAsWaitlisted(events, eventId);
						})
					);
				} catch {
					// The mutation result already exposes the request error to its caller.
				}
			},
		}),
		validateHomeEventAccess: builder.mutation<void, ValidateHomeEventAccessRequest>({
			query: (body) => ({
				url: 'events/join',
				method: 'POST',
				body,
			}),
		}),
	}),
	overrideExisting: false,
});

export default eventsApi;

export const {
	useGetEventQuery,
	useLazyGetEventQuery,
	useGetEventsQuery,
	useLazyGetEventsQuery,
	useGetUserEventsQuery,
	useLazyGetUserEventsQuery,
	useGetUserEventsSummaryQuery,
	useLazyGetUserEventsSummaryQuery,
	useGetGameGroupsQuery,
	useLazyGetGameGroupsQuery,
	useRefreshGamesListByCategoryQuery,
	useLazyRefreshGamesListByCategoryQuery,
	useCreateEventMutation,
	useUpdateEventMutation,
	useDeleteEventMutation,
	useJoinEventMutation,
	useGetPublicFeaturedEventsQuery,
	useGetFeaturedEventsForUserQuery,
	useGetPublicCrowdFavoriteEventsQuery,
	useGetCrowdFavoriteEventsForUserQuery,
	useCreateHomeWaitlistMutation,
	useValidateHomeEventAccessMutation,
} = eventsApi;
